import { Trans, useLingui } from '@lingui/react/macro';
import type { ScalingConfig, ScalingMetric } from '@repo/contracts';
import { Eye, Info, TrendingUp } from 'lucide-react';
import type { ReactNode } from 'react';

import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from '@/components/ui/tooltip';
import { cn } from '@/lib/utils';

/** UI label for the scaling-model choice; `fixed` maps to the absence of a `scaling` config. */
type ScalingKind = 'fixed' | ScalingConfig['model']['type'];
const DEFAULT_UNITS_PER_STAFF = 10;

interface ScalingControlsProps {
  /** Template demand metric — drives the labels and the written model metric. */
  metric: ScalingMetric;
  /** Base head count (Fixed mode). */
  count: number;
  onCountChange: (count: number) => void;
  /** Current scaling config, or undefined for Fixed. */
  scaling: ScalingConfig | undefined;
  onScalingChange: (scaling: ScalingConfig | undefined) => void;
}

/**
 * The per-shift "How many people?" panel shown when the template scales to
 * demand: three mode cards (Fixed / Linear / Ratio) that replace the plain count
 * field. Fixed keeps an explicit count; Linear/Ratio build a `ScalingConfig`
 * against the template metric, with optional min/max caps and (for Ratio) a
 * "one per X" rate.
 */
export function ScalingControls({
  metric,
  count,
  onCountChange,
  scaling,
  onScalingChange,
}: ScalingControlsProps) {
  const { t } = useLingui();
  const metricWord = metric === 'sales' ? t`sales` : t`guests`;
  const kind: ScalingKind = scaling?.model.type ?? 'fixed';

  const modes: Array<{ value: ScalingKind; title: string; hint: string }> = [
    { value: 'fixed', title: t`Fixed`, hint: t`Same count always` },
    { value: 'linear', title: t`Linear`, hint: t`Grows in proportion` },
    { value: 'ratio', title: t`Ratio`, hint: t`+1 per X above baseline` },
  ];

  function setKind(next: ScalingKind) {
    if (next === 'fixed') {
      onScalingChange(undefined);
      return;
    }
    const caps = capsOf(scaling);
    if (next === 'linear') {
      onScalingChange({ model: { type: 'linear', metric }, ...caps });
    } else {
      const unitsPerStaff =
        scaling?.model.type === 'ratio' ? scaling.model.unitsPerStaff : DEFAULT_UNITS_PER_STAFF;
      onScalingChange({ model: { type: 'ratio', metric, unitsPerStaff }, ...caps });
    }
  }

  function setCap(key: 'min' | 'max', value: number | undefined) {
    if (!scaling) return;
    const next = { ...scaling };
    if (value === undefined) {
      delete next[key];
    } else {
      next[key] = value;
    }
    onScalingChange(next);
  }

  function setUnits(units: number) {
    if (scaling?.model.type !== 'ratio') return;
    onScalingChange({
      ...scaling,
      model: { ...scaling.model, unitsPerStaff: Math.max(1, units || 1) },
    });
  }

  const previewHint = (() => {
    if (!scaling) return null;
    const min = scaling.min ?? 1;
    return scaling.max ? `${min}–${scaling.max}` : `${min}+`;
  })();

  return (
    <div className="space-y-3">
      <div>
        <div className="flex items-center gap-2">
          <TrendingUp className="size-4 text-muted-foreground" />
          <span className="text-sm font-semibold">
            <Trans>How many people?</Trans>
          </span>
        </div>
        <p className="mt-0.5 text-xs text-muted-foreground">
          <Trans>
            This template scales to{' '}
            <span className="font-medium text-foreground">{metricWord}</span>. Choose how this role
            responds.
          </Trans>
        </p>
      </div>

      <div className="grid grid-cols-3 gap-2">
        {modes.map((option) => (
          <button
            key={option.value}
            type="button"
            aria-pressed={kind === option.value}
            onClick={() => setKind(option.value)}
            className={cn(
              'rounded-lg border px-2 py-2.5 text-center transition-colors',
              kind === option.value ? 'border-primary bg-primary/5' : 'hover:bg-accent',
            )}
          >
            <div
              className={cn(
                'text-[length:var(--small-font-size)] leading-[var(--small-line-height)] font-semibold',
                kind === option.value ? 'text-primary' : 'text-foreground',
              )}
            >
              {option.title}
            </div>
            <div className="mt-0.5 text-[length:var(--extra-small-font-size)] leading-[var(--extra-small-line-height)] text-muted-foreground">
              {option.hint}
            </div>
          </button>
        ))}
      </div>

      {kind === 'fixed' && (
        <NumberField
          id="scaling-count"
          label={<Trans>Number of people</Trans>}
          value={count}
          min={1}
          onChange={(value) => onCountChange(Math.max(1, value ?? 1))}
        />
      )}

      {scaling?.model.type === 'linear' && (
        <div className="space-y-3">
          <NumberField
            id="scaling-base"
            label={<Trans>Staff at baseline</Trans>}
            value={count}
            min={1}
            tooltip={t`How many people you need on a normal day, when the forecast matches the baseline. Scaling adds or removes people as the forecast moves away from this.`}
            onChange={(value) => onCountChange(Math.max(1, value ?? 1))}
          />
          <RangeField
            min={scaling.min}
            max={scaling.max}
            maxPlaceholder={t`No cap`}
            minLabel={t`Minimum`}
            maxLabel={t`Maximum`}
            tooltip={t`The floor and ceiling for scaled headcount. The calculated count is clamped to this range — leave the upper field blank for no cap.`}
            onMinChange={(value) => setCap('min', value)}
            onMaxChange={(value) => setCap('max', value)}
          />
        </div>
      )}

      {scaling?.model.type === 'ratio' && (
        <div className="space-y-3">
          <div className="space-y-1">
            <Label htmlFor="scaling-units" className="text-xs font-medium text-muted-foreground">
              <Trans>Add one person per</Trans>
            </Label>
            <div className="flex items-center gap-2">
              <Input
                id="scaling-units"
                type="number"
                min={1}
                className="w-20"
                value={scaling.model.unitsPerStaff}
                onChange={(event) => setUnits(Number(event.target.value))}
              />
              <span className="text-xs text-muted-foreground">
                <Trans>{metricWord} above baseline</Trans>
              </span>
            </div>
          </div>
          <NumberField
            id="scaling-base"
            label={<Trans>Staff at baseline</Trans>}
            value={count}
            min={1}
            tooltip={t`How many people you need on a normal day, when the forecast matches the baseline. Scaling adds or removes people as the forecast moves away from this.`}
            onChange={(value) => onCountChange(Math.max(1, value ?? 1))}
          />
          <RangeField
            min={scaling.min}
            max={scaling.max}
            maxPlaceholder={t`No cap`}
            minLabel={t`Minimum`}
            maxLabel={t`Maximum`}
            tooltip={t`The floor and ceiling for scaled headcount. The calculated count is clamped to this range — leave the upper field blank for no cap.`}
            onMinChange={(value) => setCap('min', value)}
            onMaxChange={(value) => setCap('max', value)}
          />
        </div>
      )}

      {previewHint && (
        <div className="flex items-center gap-1.5 border-t pt-3 text-xs text-muted-foreground">
          <Eye className="size-3.5 shrink-0" />
          <Trans>
            Preview shows <span className="font-medium text-foreground">{previewHint}</span> per
            day.
          </Trans>
        </div>
      )}
    </div>
  );
}

interface NumberFieldProps {
  id: string;
  label: ReactNode;
  value: number | undefined;
  min?: number;
  placeholder?: string;
  /** Optional help text shown in an info tooltip next to the label. */
  tooltip?: string;
  onChange: (value: number | undefined) => void;
}

/** A small labelled number input; blank/invalid emits undefined. */
function NumberField({
  id,
  label,
  value,
  min = 0,
  placeholder,
  tooltip,
  onChange,
}: NumberFieldProps) {
  return (
    <div className="space-y-1">
      <div className="flex items-center gap-1">
        <Label htmlFor={id} className="text-xs font-medium text-muted-foreground">
          {label}
        </Label>
        {tooltip && <FieldTooltip text={tooltip} />}
      </div>
      <Input
        id={id}
        type="number"
        min={min}
        className="w-24"
        placeholder={placeholder}
        value={value ?? ''}
        onChange={(event) => onChange(parseCount(event.target.value))}
      />
    </div>
  );
}

/** An info icon with a hover/focus tooltip, used to explain a field. */
function FieldTooltip({ text }: { text: string }) {
  return (
    <TooltipProvider delayDuration={0}>
      <Tooltip>
        <TooltipTrigger asChild>
          <button
            type="button"
            aria-label={text}
            className="inline-flex text-muted-foreground hover:text-foreground"
          >
            <Info className="size-3.5" />
          </button>
        </TooltipTrigger>
        <TooltipContent className="max-w-56">{text}</TooltipContent>
      </Tooltip>
    </TooltipProvider>
  );
}

interface RangeFieldProps {
  min: number | undefined;
  max: number | undefined;
  maxPlaceholder: string;
  minLabel: string;
  maxLabel: string;
  tooltip?: string;
  onMinChange: (value: number | undefined) => void;
  onMaxChange: (value: number | undefined) => void;
}

/** A linked min↔max range: a shared "Headcount range" label over two standard
 * inputs joined by a "to" separator, so the two bounds read as one control. */
function RangeField({
  min,
  max,
  maxPlaceholder,
  minLabel,
  maxLabel,
  tooltip,
  onMinChange,
  onMaxChange,
}: RangeFieldProps) {
  return (
    <div className="space-y-1.5">
      <div className="flex items-center gap-1">
        <Label className="text-xs font-medium text-muted-foreground">
          <Trans>Headcount range</Trans>
        </Label>
        {tooltip && <FieldTooltip text={tooltip} />}
      </div>
      <div className="flex items-center gap-2">
        <Input
          type="number"
          min={0}
          aria-label={minLabel}
          className="w-24"
          value={min ?? ''}
          onChange={(event) => onMinChange(parseCount(event.target.value))}
        />
        <span className="text-xs text-muted-foreground">
          <Trans>to</Trans>
        </span>
        <Input
          type="number"
          min={0}
          aria-label={maxLabel}
          placeholder={maxPlaceholder}
          className="w-24"
          value={max ?? ''}
          onChange={(event) => onMaxChange(parseCount(event.target.value))}
        />
      </div>
    </div>
  );
}

/** Blank/non-finite/negative inputs become undefined; otherwise a non-negative integer. */
function parseCount(raw: string): number | undefined {
  if (raw.trim() === '') return undefined;
  const value = Number(raw);
  return Number.isFinite(value) && value >= 0 ? Math.floor(value) : undefined;
}

/** The currently-set caps, omitting any that are undefined. */
function capsOf(scaling: ScalingConfig | undefined): Pick<ScalingConfig, 'min' | 'max'> {
  const caps: Pick<ScalingConfig, 'min' | 'max'> = {};
  if (scaling?.min !== undefined) caps.min = scaling.min;
  if (scaling?.max !== undefined) caps.max = scaling.max;
  return caps;
}
